import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Animated, {
    SharedValue,
    interpolateColor,
    runOnJS,
    useAnimatedReaction,
    useAnimatedScrollHandler,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';

const AFlatList = Animated.createAnimatedComponent(FlatList<Values>);
const AnimatedText = Animated.createAnimatedComponent(Text);

type Values = {
    title: string;
};

type Props = {
    values: Values[];
    onChangeLine?: (index: number) => void;
    selectedTab?: number;
    scrollValue: SharedValue<number>;
};

const ITEM_WIDTH = 64;
const ITEM_HEIGHT = 46;
const MARGIN_RIGHT = 0;

const clampInterval = (value: number, lowerBound: number, upperBound: number) => {
    'worklet';
    return Math.min(Math.max(lowerBound, value), upperBound);
};

export const Dial = memo(({ values, scrollValue, onChangeLine = () => {}, selectedTab = 0 }: Props) => {
    const [hasRendered, setHasRendered] = useState(false);
    const scrollViewRef = useRef<FlatList<Values>>(null);
    const { width } = useWindowDimensions();

    const { itemsPerScreen, contentContainerPadding, tabScrollOffset } = useMemo(() => {
        const screenWidth = width - 2 * MARGIN_RIGHT;
        const padding = screenWidth / 2 - ITEM_WIDTH / 2;
        return {
            itemsPerScreen: Math.round(screenWidth / ITEM_WIDTH),
            contentContainerPadding: padding,
            tabScrollOffset: padding - screenWidth / 2 + ITEM_WIDTH / 2,
        };
    }, [width]);

    const handlePress = useCallback(
        (index: number) => {
            scrollViewRef.current?.scrollToOffset({
                offset: index * ITEM_WIDTH + tabScrollOffset,
                animated: true,
            });
        },
        [tabScrollOffset]
    );

    // ! used to manage all internal scrolling scrollValue is not provided
    const internalScrollValue = useSharedValue(0);

    const scrollHandler = useAnimatedScrollHandler({
        onScroll: event => {
            // ! sync both animation values to scroll offset
            internalScrollValue.value = event.contentOffset.x;
            if (scrollValue) {
                scrollValue.value = event.contentOffset.x;
            }
        },
    });

    const setInitialTabScroll = useCallback(() => {
        setHasRendered(true);
        scrollViewRef.current?.scrollToOffset({
            offset: selectedTab * ITEM_WIDTH + tabScrollOffset,
            animated: false,
        });
    }, [selectedTab, tabScrollOffset]);

    useAnimatedReaction(
        () => {
            const newTab = Math.round((internalScrollValue.value - tabScrollOffset * 2) / ITEM_WIDTH);
            const clampedTab = clampInterval(newTab, 0, values.length - 1);

            return clampedTab;
        },
        (currentTab, previousTab) => {
            if (hasRendered && currentTab !== previousTab) {
                onChangeLine && runOnJS(onChangeLine)(currentTab);
            }
        }
    );

    // ! make sure the selectedTab is rendered and can be centered when `onLayout` triggers
    const initialNumToRender = selectedTab + itemsPerScreen;

    return (
        <View style={styles.modalContainer}>
            <IndicatorLine />
            <AFlatList
                onScroll={scrollHandler}
                horizontal
                initialNumToRender={initialNumToRender}
                showsHorizontalScrollIndicator={false}
                ref={scrollViewRef}
                snapToInterval={ITEM_WIDTH}
                contentContainerStyle={{ paddingHorizontal: contentContainerPadding }}
                scrollEventThrottle={16}
                decelerationRate={0.0001}
                onLayout={setInitialTabScroll}
                data={values}
                renderItem={({ item, index }) => (
                    <Label
                        key={index}
                        isSelected={selectedTab === index}
                        title={item.title}
                        index={index}
                        handlePress={handlePress}
                        internalScrollValue={internalScrollValue}
                        tabScrollOffset={tabScrollOffset}
                        testID={`dialLabel-${index}`}
                    />
                )}
            />
            <IndicatorLine isBelow />
        </View>
    );
});

type LabelPropType = {
    isSelected: boolean;
    handlePress: (index: number) => void;
    title: string;
    index: number;
    internalScrollValue: SharedValue<number>;
    tabScrollOffset: number;
    testID: string;
};

const Label = memo(
    ({ isSelected, handlePress, title, index, internalScrollValue, tabScrollOffset, testID }: LabelPropType) => {
        const animatedLabelStyle = useAnimatedStyle(() => {
            const inputRange = [
                ITEM_WIDTH * (index - 1) + tabScrollOffset,
                ITEM_WIDTH * index + tabScrollOffset,
                ITEM_WIDTH * (index + 1) + tabScrollOffset,
            ];

            return {
                color: interpolateColor(internalScrollValue.value, inputRange, [
                    designSystem.colors.neutral300,
                    ds.colorValue.text.primary,
                    designSystem.colors.neutral300,
                ]),
            };
        });

        return (
            <Pressable key={index} onPress={() => handlePress(index)} accessible={false}>
                <Box width={ITEM_WIDTH} alignItems="center" height={ITEM_HEIGHT} justifyContent="center">
                    <AnimatedText
                        variant="titleLarge"
                        allowFontScaling={false}
                        style={[isSelected && styles.selectedLabel, animatedLabelStyle]}
                        testID={`alternateOption-${testID}`}
                    >
                        {title}
                    </AnimatedText>
                </Box>
            </Pressable>
        );
    }
);

const IndicatorLine = ({ isBelow }: { isBelow?: boolean }) => (
    <Box
        width="100%"
        alignItems="center"
        backgroundColor={ds.colorToken.background.neutral.subtle}
        height={1}
        flexDirection={isBelow ? 'column-reverse' : 'column'}
    >
        <Box width={ITEM_WIDTH} backgroundColor={ds.colorToken.background.brand.strong} height={2} />
    </Box>
);

const styles = StyleSheet.create({
    modalContainer: {
        paddingTop: 8,
    },
    selectedLabel: {
        fontWeight: '600',
    },
});
